import React, { useState, useMemo, useEffect } from "react";
import { Autocomplete } from "@material-ui/lab";
import { CustomInputField } from "../../../common/FormInputs";
import { FormLabel, makeStyles } from "@material-ui/core";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";
import styled from "styled-components";
import PartyService from "../../../../services/PartyService";
import debounce from "lodash/debounce";
import { CircularProgress } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";

const useStyles = makeStyles((theme) => ({
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    "& li[data-option-index]": {
      padding: "12px 16px !important",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "#f0f9ff",
        paddingLeft: "20px !important",
      },
    },
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
    padding: "16px",
    fontSize: "14px",
  },
  paper: {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
    borderRadius: "8px !important",
    border: "1px solid #e2e8f0 !important",
  },
}));

const PAGE_SIZE = 50;

const CustomSelect = ({
  name,
  errors,
  touched,
  setFieldValue,
  menuItems,
  getNoticeTemplates,
  trigger,
  onPartySelect,
  value,
  required = true,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!value) {
      setInputValue("");
      setOptions([]);
    }
  }, [value]);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (searchTerm, currentPage) => {
        try {
          setLoading(true);
          const queryParams = `?search=${encodeURIComponent(
            searchTerm,
          )}&page=${currentPage}&perPage=${PAGE_SIZE}&status=active`;
          const response = await PartyService.index(queryParams);

          if (response?.data?.length) {
            const newOptions = response.data.map(({ name, id, owner }) => ({
              label: name,
              value: id,
              email: owner?.email,
            }));
            setOptions((prev) =>
              currentPage === 1 ? newOptions : [...prev, ...newOptions],
            );
            setTotal(response.meta?.total || total);
          }
        } catch (error) {
          enqueueSnackbar(getErrorMessage(error), { variant: "error" });
          if (error.response?.status >= 500) {
            setTimeout(() => debouncedFetch(searchTerm, currentPage), 1000);
          }
        } finally {
          setLoading(false);
        }
      }, 300), // eslint-disable-next-line react-hooks/exhaustive-deps
    [total],
  );

  useEffect(() => {
    if (open && inputValue.length >= 2) {
      setOptions([]);
      debouncedFetch(inputValue, 1);
    } else if (!inputValue) {
      setOptions([]);
      window.scrollTo(0, 0);
    }
  }, [inputValue, open, debouncedFetch]);

  const handleKeyDown = (event) => {
    if (event.key === "Tab") {
      if (menuItems.length > 0 && event?.target?.value?.length) {
        const filteredData = menuItems.filter((item) =>
          item.label.toLowerCase().includes(event?.target?.value.toLowerCase()),
        );
        if (filteredData?.length) {
          setFieldValue(name, filteredData[0]?.value);
          if (trigger) {
            // getNoticeTemplates(filteredData[0]?.value);
            if (onPartySelect) {
              onPartySelect(filteredData[0]?.label, filteredData[0]?.value);
            } else {
              getNoticeTemplates(filteredData[0]?.value);
            }
          }
          setOpen(false);
        }
      }
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue === null) {
      setFieldValue(name, "");
      setInputValue("");
    } else {
      if (trigger) {
        // getNoticeTemplates(newValue?.value);

        if (onPartySelect) {
          onPartySelect(newValue?.label, newValue?.value);
        } else {
          getNoticeTemplates(newValue?.value);
        }
      }
      setFieldValue(name, newValue?.value);
      setInputValue(newValue?.label);
    }
  };

  return (
    <>
      <SelectWrapper className="form-fields">
        <SelectLabel
          error={errors[name] && touched[name]}
          style={{
            fontFamily: theme.fonts.primaryFontSemiBold,
            fontSize: 12,
            color: COLORS.INPUT_LABEL,
          }}
        >
          Select a Party
          {required && <RequiredStar> *</RequiredStar>}
        </SelectLabel>

        <StyledAutocomplete
          classes={{
            listbox: classes.listbox,
            noOptions: classes.noOptions,
            paper: classes.paper,
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={(event, value) => setInputValue(value)}
          selectOnFocus
          clearOnBlur
          options={options}
          loading={loading}
          noOptionsText={
            inputValue.length < 2
              ? "Type at least 2 characters to search"
              : loading
                ? "Searching..."
                : "No parties found"
          }
          getOptionLabel={(option) => option.label && option.label.toString()}
          renderOption={(option) => (
            <OptionContainer>
              <OptionContent>
                <OptionName>{option.label}</OptionName>
                <OptionEmail>{option.email}</OptionEmail>
              </OptionContent>
              <OptionCheck>✓</OptionCheck>
            </OptionContainer>
          )}
          renderInput={(params) => (
            <CustomInputField
              {...params}
              variant="outlined"
              placeholder="Type to search parties..."
              error={errors[name] && touched[name]}
              helperText={touched[name] && errors[name]}
              onKeyDown={handleKeyDown}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </SelectWrapper>
    </>
  );
};

export default CustomSelect;

export const SelectWrapper = styled.div`
  width: 100%;
  padding-top: 5px;
`;

export const SelectLabel = styled(FormLabel)`
  display: flex;
  font-family: ${(props) => props.theme?.fonts?.primaryFontSemiBold};
  font-size: 12px;
  color: ${COLORS.INPUT_LABEL};
  margin-bottom: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

export const RequiredStar = styled.span`
  color: #ef4444;
  margin-left: 4px;
  font-weight: 900;
`;

export const StyledAutocomplete = styled(Autocomplete)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
    transition: all 0.3s ease;
    background: white;

    &:hover {
      border-color: #cbd5e1;
    }

    &.Mui-focused {
      box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
    }
  }

  .MuiInputBase-input {
    font-size: 16px !important;
    font-weight: 500 !important;
    color: #293461 !important;

    &::placeholder {
      color: #a0aec0;
      opacity: 1;
    }
  }
`;

export const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0;
`;

export const OptionContent = styled.div`
  flex: 1;
`;

export const OptionName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #293461;
  font-family: openSans-SemiBold, sans-serif;
  margin-bottom: 4px;
`;

export const OptionEmail = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  font-family: openSans-SemiBold, sans-serif;
`;

export const OptionCheck = styled.span`
  font-size: 16px;
  color: #00838c;
  opacity: 0;
  transition: opacity 0.2s ease;

  li[data-option-index]:hover & {
    opacity: 1;
  }
`;
